import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { formatAmount } from "../../utils";
import { addBond, editBond, getAllBonds } from "../../redux/bonds/bondSlice";
import toast from "react-hot-toast";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  AddListingButton,
  bodyCellSx,
  CategoryPill,
  DetailInfoGrid,
  EditAction,
  EmptyTableRow,
  ExpandButton,
  ListingAvatar,
  ListingPageShell,
  headCellSx,
  primaryButtonSx,
  SortableHeader,
} from "../../components/AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT } from "../../constants/layout";

const ISIN_LENGTH = 12; // e.g. INE002A07809
const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const UNKNOWN_TOTAL = -1; // MUI renders "more than N" when the API omits a total.

const INTEREST_PAYMENT_FREQUENCIES = [
  "Once a year",
  "On Maturity",
  "QUARTERLY",
  "Four times a year",
  "No Occurence",
  "Twice a year",
  "Twelve times a year",
  "At Any Time",
  "Irregular Occurence",
  "On Maturity/Put-Call",
  "Every 5 years",
  "Every 3 years",
  "Every 2 years",
  "Every 7 years",
  "Thrice a year",
  "Every 10 years",
  "Weekly",
  "Every 15 years",
  "Zero Coupon",
  "7 times in years",
  "Every 6 years",
  "ANNUAL",
  "Monthly",
  "HALF YEARLY",
];

const BOND_CATEGORIES = [
  "Taxable",
  "Taxfree",
  "Perpetual debt instruments",
  "Plain Vanilla Debentures",
  "PERPETUAL",
];

const ISSUE_CATEGORIES = [
  "Corporate",
  "Financial Institution",
  "Private Placement",
  "Private Placement (EBP)",
  "Private Placement (non-EBP)",
  "Private",
  "Soverign",
];

const BOND_SUB_CATEGORIES = [
  "Private",
  "NBFC",
  "PSU",
  "Bank",
  "NBFCs",
  "State Bond",
  "Others",
  "Central Government Bond",
  "SDL",
  "Treasury Bill",
];

type BondFormData = {
  bondId: number | string;
  bondISIN: string;
  bondCategory: string;
  bondSubCategory: string;
  issueCategory: string;
  creditRating: string;
  faceValue: string;
  bondPrice: string;
  couponRate: string;
  interestPaymentFrequency: string;
  firstInterestPaymentDate: string;
  allotmentDate: string;
  maturityDate: string;
  callDate: string;
  promoter: string;
};

const emptyForm: BondFormData = {
  bondId: "",
  bondISIN: "",
  bondCategory: "",
  bondSubCategory: "",
  issueCategory: "",
  creditRating: "",
  faceValue: "",
  bondPrice: "",
  couponRate: "",
  interestPaymentFrequency: "",
  firstInterestPaymentDate: "",
  allotmentDate: "",
  maturityDate: "",
  callDate: "",
  promoter: "",
};

// The API may send dates as DD/MM/YYYY, ISO strings or epoch millis; date inputs need YYYY-MM-DD.
const toDateInputValue = (value: any) => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "";
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split("/");
    return `${year}-${month}-${day}`;
  }
  const date = /^\d+$/.test(raw) ? new Date(Number(raw)) : new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

// The payload and the table both use DD/MM/YYYY; native date inputs only speak YYYY-MM-DD.
const toApiDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const displayDate = (value: any) => toApiDate(toDateInputValue(value)) || "-";

const displayValue = (value: any) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const extractBondList = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const candidate =
    payload.bondList || payload.bonds || payload.data?.bondList || payload.data;
  return Array.isArray(candidate) ? candidate : [];
};

const extractTotalCount = (payload: any): number => {
  const total =
    payload?.totalCount ??
    payload?.totalRecords ??
    payload?.totalEntries ??
    payload?.count ??
    payload?.total ??
    payload?.data?.totalCount;
  const parsed = Number(total);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : UNKNOWN_TOTAL;
};

const BondField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  multiline = false,
  fullSpan = false,
  disabled = false,
  error = false,
  helperText,
  inputProps,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  multiline?: boolean;
  fullSpan?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  inputProps?: Record<string, any>;
}) => (
  <TextField
    className={fullSpan ? "col-span-2" : undefined}
    label={label}
    variant="outlined"
    fullWidth
    margin="normal"
    name={name}
    type={type}
    disabled={disabled}
    multiline={multiline}
    minRows={multiline ? 3 : undefined}
    error={error}
    helperText={helperText}
    onChange={(e) => onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
    inputProps={inputProps}
    value={value || ""}
  />
);

const BondSelectField = ({
  label,
  id,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  id: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  // Keep an unrecognised saved value selectable so editing a bond can't silently drop it.
  const items = value && !options.includes(value) ? [...options, value] : options;

  return (
    <FormControl fullWidth variant="outlined" margin="normal" disabled={disabled}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {items.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const BondTableRow = ({
  data,
  columnCount,
  onEdit,
}: {
  data: any;
  columnCount: number;
  onEdit: (data: any) => void;
}) => {
  const [open, setOpen] = useState(false);
  const userType = Number(localStorage.getItem("userType")) || 0;
  const isin = data?.isin || data?.bondISIN || "";

  if (!isin || isin === "-" || isin.trim() === "") return null;

  return (
    <>
      <TableRow hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ListingAvatar name={isin} />
            <Box>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: DARK_NAVY }}>
                {isin}
              </Typography>
              {data.bondSubCategory ? (
                <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                  {data.bondSubCategory}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <CategoryPill label={data.bondCategory || "-"} />
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY }}>
            {displayValue(data.couponRate)}
          </Typography>
          {data.interestPaymentFrequency ? (
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
              {data.interestPaymentFrequency}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
          {data.faceValue ? formatAmount(data.faceValue) : "-"}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
          {data.bondPrice ? formatAmount(data.bondPrice) : "-"}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
          {displayDate(data.maturityDate)}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
          {data.creditRating || "-"}
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ExpandButton open={open} onClick={() => setOpen(!open)} />
        </TableCell>
        {userType === 4 && (
          <TableCell sx={bodyCellSx} align="center">
            <EditAction onClick={() => onEdit(data)} />
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={columnCount}
          sx={{ py: 0, borderBottom: open ? "1px solid #F5F5F5" : "none" }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: { xs: 1, md: 2 }, py: 1.5 }}>
              <DetailInfoGrid
                items={[
                  { label: "ISIN", value: displayValue(isin) },
                  { label: "Bond Category", value: displayValue(data.bondCategory) },
                  { label: "Bond Sub Category", value: displayValue(data.bondSubCategory) },
                  { label: "Issue Category", value: displayValue(data.issueCategory) },
                  { label: "Credit Rating", value: displayValue(data.creditRating) },
                  {
                    label: "Face Value",
                    value: data.faceValue ? formatAmount(data.faceValue) : "-",
                  },
                  {
                    label: "Bond Price",
                    value: data.bondPrice ? formatAmount(data.bondPrice) : "-",
                  },
                  { label: "Coupon Rate", value: displayValue(data.couponRate) },
                  {
                    label: "Interest Payment Frequency",
                    value: displayValue(data.interestPaymentFrequency),
                  },
                  {
                    label: "First Interest Payment Date",
                    value: displayDate(data.firstInterestPaymentDate),
                  },
                  { label: "Allotment Date", value: displayDate(data.allotmentDate) },
                  { label: "Maturity Date", value: displayDate(data.maturityDate) },
                  { label: "Call Date", value: displayDate(data.callDate) },
                  { label: "Promoter", value: displayValue(data.promoter) },
                ]}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BondListing = () => {
  const dispatch = useDispatch<any>();
  const userType = Number(localStorage.getItem("userType")) || 0;
  const [loader, setLoader] = React.useState(true);
  const [showBondModal, setShowBondModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [bondData, setBondData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(UNKNOWN_TOTAL);
  const [formData, setFormData] = useState<BondFormData>(emptyForm);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string | null;
  }>({
    key: "isin",
    direction: "ascending",
  });

  const columnCount = userType === 4 ? 9 : 8;

  const fetchData = async () => {
    setLoader(true);
    try {
      const requestBody: Record<string, any> = { pageIndex, pageSize };
      if (searchQuery) requestBody.isin = searchQuery;

      const response = await dispatch(getAllBonds(requestBody));

      if (response) {
        const payload = response?.payload?.data;
        setBondData(extractBondList(payload));
        setTotalCount(extractTotalCount(payload));
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize, searchQuery]);

  // Debounce so typing an ISIN doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim().toUpperCase());
      setPageIndex(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getSortedData = useCallback(() => {
    if (!sortConfig.key) return bondData;

    const sortedData = [...bondData];
    const order = sortConfig.direction === "ascending" ? 1 : -1;

    switch (sortConfig.key) {
      case "isin":
      case "bondCategory":
      case "creditRating":
        sortedData.sort((a, b) => {
          const valA = (a[sortConfig.key] || "").toString().toUpperCase();
          const valB = (b[sortConfig.key] || "").toString().toUpperCase();
          return (valA < valB ? -1 : valA > valB ? 1 : 0) * order;
        });
        break;
      case "couponRate":
      case "faceValue":
      case "bondPrice":
        sortedData.sort(
          (a, b) =>
            ((Number(a[sortConfig.key]) || 0) -
              (Number(b[sortConfig.key]) || 0)) *
            order
        );
        break;
      case "maturityDate":
        sortedData.sort((a, b) => {
          const dateA = new Date(toDateInputValue(a.maturityDate) || 0).getTime();
          const dateB = new Date(toDateInputValue(b.maturityDate) || 0).getTime();
          return (dateA - dateB) * order;
        });
        break;
      default:
        break;
    }

    return sortedData;
  }, [bondData, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const sortedData = getSortedData();

  const setField = (key: keyof BondFormData) => (value: string) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const handleClose = () => {
    setShowBondModal(false);
    setIsEdit(false);
    setFormData(emptyForm);
  };

  const handleAdd = () => {
    setFormData(emptyForm);
    setIsEdit(false);
    setShowBondModal(true);
  };

  const handleEdit = (data: any) => {
    setFormData({
      bondId: data?.bondId ?? data?.id ?? "",
      bondISIN: data?.isin || data?.bondISIN || "",
      bondCategory: data?.bondCategory || "",
      bondSubCategory: data?.bondSubCategory || "",
      issueCategory: data?.issueCategory || "",
      creditRating: data?.creditRating || "",
      faceValue: data?.faceValue?.toString() || "",
      bondPrice: data?.bondPrice?.toString() || "",
      couponRate: data?.couponRate?.toString() || "",
      interestPaymentFrequency: data?.interestPaymentFrequency || "",
      firstInterestPaymentDate: toDateInputValue(data?.firstInterestPaymentDate),
      allotmentDate: toDateInputValue(data?.allotmentDate),
      maturityDate: toDateInputValue(data?.maturityDate),
      callDate: toDateInputValue(data?.callDate),
      promoter: data?.promoter || "",
    });
    setIsEdit(true);
    setShowBondModal(true);
  };

  const handleAddBond = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        addBond({
          bondISIN: formData.bondISIN,
          callDate: toApiDate(formData.callDate),
          faceValue: formData.faceValue,
          bondPrice: formData.bondPrice,
          couponRate: formData.couponRate,
          maturityDate: toApiDate(formData.maturityDate),
          bondCategory: formData.bondCategory,
          creditRating: formData.creditRating,
          issueCategory: formData.issueCategory,
          allotmentDate: toApiDate(formData.allotmentDate),
          bondSubCategory: formData.bondSubCategory,
          interestPaymentFrequency: formData.interestPaymentFrequency,
          firstInterestPaymentDate: toApiDate(formData.firstInterestPaymentDate),
          promoter: formData.promoter,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding bond.");
        return;
      }

      toast.success("Bond added successfully.");
      handleClose();
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const handleEditBond = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        editBond({
          bondId: formData.bondId,
          bondISIN: formData.bondISIN,
          callDate: toApiDate(formData.callDate),
          faceValue: formData.faceValue,
          couponRate: formData.couponRate,
          maturityDate: toApiDate(formData.maturityDate),
          interestPaymentFrequency: formData.interestPaymentFrequency,
          firstInterestPaymentDate: toApiDate(formData.firstInterestPaymentDate),
        })
      );
      if (response?.error?.message) {
        toast.error("Error updating bond.");
        return;
      }

      toast.success("Bond updated successfully.");
      handleClose();
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const isIsinInvalid =
    !isEdit &&
    formData.bondISIN.length > 0 &&
    formData.bondISIN.length !== ISIN_LENGTH;

  const isSubmitDisabled =
    // On edit the ISIN is read-only, so an odd legacy value must not block the save.
    (isEdit ? !formData.bondISIN : formData.bondISIN.length !== ISIN_LENGTH) ||
    !formData.faceValue ||
    !formData.couponRate ||
    !formData.interestPaymentFrequency ||
    !formData.maturityDate ||
    (!isEdit && (!formData.bondCategory || !formData.promoter));

  return (
    <>
      <AppLoader loading={loader} />
      <ListingPageShell
        title="Bonds"
        subtitle="Browse and manage bonds available on the platform."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search bonds by ISIN..."
        action={
          userType === 4 ? (
            <AddListingButton label="Add Bond" onClick={handleAdd} />
          ) : undefined
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="ISIN" onSort={() => handleSort("isin")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Category" onSort={() => handleSort("bondCategory")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Coupon Rate" onSort={() => handleSort("couponRate")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Face Value" onSort={() => handleSort("faceValue")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Bond Price" onSort={() => handleSort("bondPrice")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Maturity Date" onSort={() => handleSort("maturityDate")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Credit Rating" onSort={() => handleSort("creditRating")} />
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: 72 }} align="center">
                  Details
                </TableCell>
                {userType === 4 && (
                  <TableCell sx={{ ...headCellSx, width: 88 }} align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <EmptyTableRow colSpan={columnCount} />
                </TableRow>
              ) : (
                sortedData.map((row: any, index: number) => (
                  <BondTableRow
                    key={row.id ?? row.bondId ?? index}
                    data={row}
                    columnCount={columnCount}
                    onEdit={handleEdit}
                  />
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                  colSpan={columnCount}
                  count={totalCount}
                  rowsPerPage={pageSize}
                  page={pageIndex - 1}
                  onPageChange={(_, newPage) => setPageIndex(newPage + 1)}
                  onRowsPerPageChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPageIndex(1);
                  }}
                  // Without a total the last page is only detectable by a short result set.
                  nextIconButtonProps={
                    totalCount === UNKNOWN_TOTAL
                      ? { disabled: bondData.length < pageSize }
                      : undefined
                  }
                  sx={{ fontFamily: PROFILE_FONT, borderBottom: "none" }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </ListingPageShell>
      <Modal open={showBondModal} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            maxHeight: "80vh",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              {isEdit ? "Edit Bond" : "Add Bond"}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Box component="form" noValidate autoComplete="off">
            <div className="grid grid-cols-2 gap-4">
              <BondField
                label="Bond ISIN *"
                name="bondISIN"
                value={formData.bondISIN}
                disabled={isEdit}
                error={isIsinInvalid}
                helperText={
                  isEdit
                    ? "ISIN cannot be changed."
                    : `${ISIN_LENGTH} characters, e.g. INE002A07809`
                }
                inputProps={{ maxLength: ISIN_LENGTH }}
                onChange={(value) =>
                  setField("bondISIN")(
                    value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, ISIN_LENGTH)
                  )
                }
              />
              {!isEdit && (
                <>
                  <BondSelectField
                    label="Bond Category *"
                    id="bond-category"
                    options={BOND_CATEGORIES}
                    value={formData.bondCategory}
                    onChange={setField("bondCategory")}
                  />
                  <BondSelectField
                    label="Bond Sub Category"
                    id="bond-sub-category"
                    options={BOND_SUB_CATEGORIES}
                    value={formData.bondSubCategory}
                    onChange={setField("bondSubCategory")}
                  />
                  <BondSelectField
                    label="Issue Category"
                    id="issue-category"
                    options={ISSUE_CATEGORIES}
                    value={formData.issueCategory}
                    onChange={setField("issueCategory")}
                  />
                  <BondField
                    label="Credit Rating"
                    name="creditRating"
                    value={formData.creditRating}
                    onChange={setField("creditRating")}
                  />
                  <BondField
                    label="Bond Price"
                    name="bondPrice"
                    type="number"
                    value={formData.bondPrice}
                    onChange={setField("bondPrice")}
                  />
                </>
              )}
              <BondField
                label="Face Value *"
                name="faceValue"
                type="number"
                value={formData.faceValue}
                onChange={setField("faceValue")}
              />
              <BondField
                label="Coupon Rate *"
                name="couponRate"
                value={formData.couponRate}
                onChange={setField("couponRate")}
              />
              <BondSelectField
                label="Interest Payment Frequency *"
                id="interest-payment-frequency"
                options={INTEREST_PAYMENT_FREQUENCIES}
                value={formData.interestPaymentFrequency}
                onChange={setField("interestPaymentFrequency")}
              />
              <BondField
                label="First Interest Payment Date"
                name="firstInterestPaymentDate"
                type="date"
                value={formData.firstInterestPaymentDate}
                onChange={setField("firstInterestPaymentDate")}
              />
              {!isEdit && (
                <BondField
                  label="Allotment Date"
                  name="allotmentDate"
                  type="date"
                  value={formData.allotmentDate}
                  onChange={setField("allotmentDate")}
                />
              )}
              <BondField
                label="Maturity Date *"
                name="maturityDate"
                type="date"
                value={formData.maturityDate}
                onChange={setField("maturityDate")}
              />
              <BondField
                label="Call Date"
                name="callDate"
                type="date"
                value={formData.callDate}
                onChange={setField("callDate")}
              />
              {!isEdit && (
                <BondField
                  label="Promoter *"
                  name="promoter"
                  multiline
                  fullSpan
                  value={formData.promoter}
                  onChange={setField("promoter")}
                />
              )}
            </div>
            <div className="mt-4">
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  color: DARK_NAVY_LIGHT,
                  mb: 1.5,
                }}
              >
                * are required fields, rest optional
              </Typography>
              <Button
                onClick={() => (isEdit ? handleEditBond() : handleAddBond())}
                sx={primaryButtonSx}
                disabled={isSubmitDisabled}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default BondListing;
